/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/

//!!OA import "common/samples-common.scss";
import { IModelApp, IModelConnection, ScreenViewport, ViewState3d } from "@itwin/core-frontend";
import { ViewClipClearTool, ViewClipDecorationProvider, ViewClipTool } from "@itwin/core-frontend";
import { ClipMaskXYZRangePlanes,  ConvexClipPlaneSet } from "@itwin/core-geometry";
import { ClipPrimitive, ClipShape, ClipUtilities, ClipVector } from "@itwin/core-geometry";
import { Point3d, Range3d, Vector3d } from "@itwin/core-geometry";

import { cQuery } from "./Query";
import { cView } from "./View";
import { cTime } from "./time";
import { cSelection } from "./Selection";

export class cViewClip {
  private static iModel: IModelConnection;
  private static sViewPort: ScreenViewport;
  private static fitRange: Range3d;
  private static fitX: number;
  private static fitY: number;
  private static vcdp: ViewClipDecorationProvider;

  /* Setup the clip box */
  public static initClipping(vp: ScreenViewport, imodel: IModelConnection) {
    this.iModel = imodel;
    this.sViewPort = vp;

    if (undefined === cView.getCurrentView()) {
      return;
    }

    // store start up view properties
    const view3d = vp.view as ViewState3d;
    this.fitRange = view3d.computeFitRange();
    const frustum = vp.getFrustum();
    //    view3d.setupFromFrustum(frustum);
    this.fitX = view3d.extents.x;
    this.fitY = view3d.extents.y;
    // this.clipToRange(this.fitRange);
    // this.fitRange.setFrom(this.getClipRange());

    this.vcdp = ViewClipDecorationProvider.create();

    //    cMarker.createMarkerSet("Clip", cIssue.drawCallback, cIssue.onPinSelected);  //!!OA

    // Call enableClipVolume to ensure all clip flags are properly set
    ViewClipTool.enableClipVolume(vp);
    cViewClip.clearClips();
  }

  public static async elementClippingTool(elementId: string) {
    const range = await cQuery.getElementRange(elementId);
    await this.startClippingTool(range);
  }

  public static async startClippingTool(region?: Range3d) {
    const range = region ?? Range3d.createNull();
    const vp = cView.getCurrentView();

    // if region is not provided, start with the existing clipped area
    if (!range || range.isNull) {
      range.setFrom(await this.clipToSelection());
      if (!range || range.isNull) range.setFrom(this.getClipRange());
      // if there is no clipped area //, start with clip box within current view volume
      if (range && range.isNull) {
        const view3d = vp.view as ViewState3d;
        // current view volume is calculated by scaling model extent range by
        // ratio of current frustrum size to extent frusturm size (based on frustrum y)
        const center = vp.getFrustum().getCenter();
        // const markerData = new MarkerData;
        // markerData.point = center.clone();

        // cMarker.addMarkerPoint("Clip", markerData);
        if (!this.fitRange || this.fitRange.isNull) {
          this.fitRange = view3d.computeFitRange();
          this.fitX = view3d.extents.x;
          this.fitY = view3d.extents.y;
          range.setFrom(this.fitRange);
        } else {
          const frustum = vp.getFrustum();
          const currX = view3d.extents.x;
          const center = vp.getFrustum().getCenter();
          const shift = center.minus(this.fitRange.center);
          range.setFrom(this.fitRange.cloneTranslated(shift));
          range.scaleAboutCenterInPlace(currX / this.fitX);
        }
      //   range.setFrom(view3d.computeFitRange());
      }
    }
    //if (!this.sViewPort.view.getViewClip()) {
    // Get the range of the model contents.
   //   const range = vp.view.computeFitRange();

      cViewClip.clipToRange(range);
      cViewClip.addDecorators(vp);
   // }
  }

  /* Method for clearing all clips in the viewport */
  public static clearClips() {
    // Run the ViewClipClearTool
//    IModelApp.tools.run(ViewClipClearTool.toolId);
    const curVp = cView.getCurrentView();
    curVp.view.setViewClip();
//!!OA workaround as setViewClip() doesn't call sync function
//    curVp.invalidateScene();
    curVp.invalidateController();  
    // const pose = curVp.view.savePose();
    // curVp.setupFromView(pose);
//!!OA 
    this.stopClippingTool();
    if (this.fitRange) this.fitRange.setNull();
  }

  public static stopClippingTool() {
    // hide the decorators
    //ViewClipDecorationProvider.create().toggleDecoration(this.sViewPort);
    this.vcdp.toggleDecoration(cView.getCurrentView());
  }

  /* Method for adding decorators to the viewport */
  private static addDecorators(vp: ScreenViewport) {
    // Create a clip decorator. Selecting the clip decoration to immediately show the handles is the default.
    //    const vcdp: ViewClipDecorationProvider = ViewClipDecorationProvider.create();
    //    this.vcdp = ViewClipDecorationProvider.create();
    // Default behavior is to hide the decorators on deselect. We want to keep the decorators showing in this example.
    this.vcdp.clearDecorationOnDeselect = false;
    this.vcdp.showDecoration(vp);
    // The decorators require the SelectTool being active.
    IModelApp.toolAdmin.startDefaultTool();
  }

  /* Method for adding a new clip range around a range */
  public static clipToRange = (range: Range3d) => {
    if (!range || range.isNull) return;

    // Create a block for the ClipVector
    const block: ClipShape = ClipShape.createBlock(
      range,
      range.isAlmostZeroZ
        ? ClipMaskXYZRangePlanes.XAndY
        : ClipMaskXYZRangePlanes.All,
      false,
      false
    );
    // const segmentPlanes = block.fetchClipPlanesRef();
    // const planeSets = UnionOfConvexClipPlaneSets.createEmpty();

    // console.log(`clipToRange: [${time.getElapsed()}] block created`);
    // for (const convexSet of segmentPlanes!.convexSets)
    //   planeSets.addConvexSet(convexSet);

    // const primitive = ClipPrimitive.createCapture(planeSets);
    // const cVector = ClipVector.createCapture([primitive]);

    // console.log(cVector);

    // Create the ClipVector
    const clip: ClipVector = ClipVector.createEmpty();
    // Add the block to the Clipvector and set it in the ScreenViewport
    clip.appendReference(block);
    // Call enableClipVolume to ensure all clip flags are properly set
    //    ViewClipTool.enableClipVolume(cViewClip.sViewPort);

    const vp = cView.getCurrentView();
    vp.view.viewFlags = vp.view.viewFlags.with("clipVolume", true);
    vp.view.setViewClip(clip);
  };

  /* Method for getting a normal vector. */
  //x  private static getPlaneInwardNormal(orientation: EditManipulator.RotationType, viewport: Viewport): Vector3d | undefined {
  //x    // const matrix = EditManipulator.HandleUtils.getRotation(orientation, viewport);
  //x    // if (undefined === matrix)
  //x    //   return undefined;
  //x    // return matrix.getColumn(2).negate();
  //x  }

  public static setViewClipFromClipPlaneSet(vp: ScreenViewport, planeSet: ConvexClipPlaneSet) {
    const prim = ClipPrimitive.createCapture(planeSet);
    const clip = ClipVector.createEmpty();
    clip.appendReference(prim);
    vp.view.viewFlags = vp.view.viewFlags.with("clipVolume", true);
    vp.view.setViewClip(clip);
    vp.setupFromView();
    this.addDecorators(vp);
    return true;
  }

  /* Method for setting a plane as the view clip */
  public static setClipPlane(vp: ScreenViewport, clipPlane: string, imodel: IModelConnection) {
    //x    let rotationType: EditManipulator.RotationType;
    //x    switch (clipPlane) {
    //x      default:
    //x      case "0": rotationType = EditManipulator.RotationType.Top; break;
    //x      case "1": rotationType = EditManipulator.RotationType.Front; break;
    //x      case "2": rotationType = EditManipulator.RotationType.Left; break;
    //x      case "None": return true;
    //x    }
    // Get the center point of the displayed extents as a starting point for the clip plane
    //x    const point: Point3d = imodel.displayedExtents.center;
    //x    const normal: Vector3d | undefined = this.getPlaneInwardNormal(rotationType, vp);
    //x    const plane: Plane3dByOriginAndUnitNormal | undefined = Plane3dByOriginAndUnitNormal.create(point, normal!);
    //x    if (undefined === plane)
    //x      return false;
    //x    let planeSet: ConvexClipPlaneSet | undefined;
    //x    if (undefined === planeSet)
    //x      planeSet = ConvexClipPlaneSet.createEmpty();
    //x    planeSet.addPlaneToConvexSet(ClipPlane.createPlane(plane));
    //x    return this.setViewClipFromClipPlaneSet(vp, planeSet);
  }

  public static async clipToElement(elementId: string): Promise<Range3d | undefined> {
    if (!elementId) return;

    const range = await cQuery.getElementRange(elementId);
    if (range && !range.isNull) {
      range.expandInPlace(0.4);
      this.clipToRange(range);
      cView.zoomToRange(range);
    }

    return range;
  }

  // return current clipping range
  public static getClipRange(): Range3d {
    const range = Range3d.createNull();
    const vp = cView.getCurrentView();
    const clip = vp.view.getViewClip();
    if (undefined !== clip) {
      //!!OA view      const maxRange = this.sViewPort.computeViewRange();
      const maxRange = vp.computeViewRange();  //!!OA view
      const clipRange = ClipUtilities.rangeOfClipperIntersectionWithRange(clip, maxRange );
      if (!clipRange.isNull) range.setFrom(clipRange);
    }

    return range;
  }

  public static async clipToSelection(): Promise<Range3d> {
//    const elements = this.iModel.selectionSet.elements;
    const elements = cSelection.getSelections();
    const vp = cView.getCurrentView();

   const selRange = Range3d.createNull();
    for (const elementId of elements) {
      const range = await cQuery.getElementRange(elementId);
      if (range && !range.isNull) {
        range.expandInPlace(0.1);
        selRange.extendRange(range);
      }
    }

    if (!selRange.isNull) {
      this.clipToRange(selRange);
      cView.zoomToRange(selRange);
    }

    return selRange;
  }

  public static expandClipRange(factor: number, expRange?: Range3d) {
    var range: Range3d;
    if (expRange && !expRange.isNull) {
      range = expRange.clone();
    } else {
      range = this.getClipRange();
    }

    if (range && !range.isNull) {
      range.expandInPlace(factor);
      cViewClip.clipToRange(range);
      cView.zoomToRange(range);
    }
  }
}
